import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { createReview } from '../../services/patientService';
import Avatar from './Avatar';

export default function ReviewModal({ appointment, doctor, onClose, onDone }) {
  const [rating, setRating]   = useState(0);
  const [hover, setHover]     = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
  const COLORS = ['', 'text-red-500', 'text-orange-400', 'text-amber-400', 'text-lime-500', 'text-green-500'];

  const handleSubmit = async () => {
    if (!rating) { toast.error('Please select a rating'); return; }
    setLoading(true);
    try {
      await createReview({ appointmentId: appointment._id, rating, comment });
      setSubmitted(true);
      setTimeout(() => { onDone?.(); onClose(); }, 1800);
    } catch (err) {
      toast.error(err.message || 'Already reviewed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center"
        onClick={onClose}>
        <div
          className="bg-white w-full max-w-sm rounded-t-3xl sm:rounded-2xl p-6 shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {submitted ? (
            /* ── Success state ── */
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fa-solid fa-check text-success text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-text-primary font-heading">Thank You!</h3>
              <p className="text-sm text-text-muted mt-1">Your review helps others choose the right doctor</p>
            </div>
          ) : (
            <>
              {/* Handle bar */}
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5 sm:hidden" />

              {/* Doctor info */}
              <div className="flex items-center gap-3 mb-5">
                <Avatar src={doctor?.profileImg} name={doctor?.name} size="md" />
                <div>
                  <p className="font-bold text-text-primary">{doctor?.name}</p>
                  <p className="text-xs text-primary">{doctor?.speciality}</p>
                </div>
                <button onClick={onClose} className="ml-auto btn-icon">
                  <i className="fa-solid fa-xmark text-text-secondary" />
                </button>
              </div>

              <h3 className="text-base font-bold text-text-primary font-heading text-center mb-1">
                How was your experience?
              </h3>
              <p className="text-xs text-text-muted text-center mb-5">
                Rate your appointment with {doctor?.name}
              </p>

              {/* Star rating */}
              <div className="flex justify-center gap-3 mb-2">
                {[1,2,3,4,5].map(star => (
                  <button
                    key={star}
                    onMouseEnter={() => setHover(star)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(star)}
                    className="transition-transform hover:scale-110 active:scale-95"
                  >
                    <i className={`fa-star text-3xl transition-colors ${
                      star <= (hover || rating)
                        ? 'fa-solid text-amber-400'
                        : 'fa-regular text-gray-200'
                    }`} />
                  </button>
                ))}
              </div>

              {/* Rating label */}
              <p className={`text-center text-sm font-bold mb-4 h-5 transition-all ${
                COLORS[hover || rating] || 'text-transparent'
              }`}>
                {LABELS[hover || rating] || '—'}
              </p>

              {/* Comment */}
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Share your experience (optional)..."
                rows={3}
                className="input resize-none text-sm mb-4"
              />

              {/* Buttons */}
              <div className="flex gap-3">
                <button onClick={onClose}
                  className="flex-1 py-3 border border-border rounded-btn text-sm font-medium text-text-secondary">
                  Skip
                </button>
                <button onClick={handleSubmit} disabled={loading || !rating}
                  className={`flex-1 py-3 rounded-btn text-sm font-semibold transition-all ${
                    rating ? 'bg-primary text-white' : 'bg-gray-100 text-text-muted cursor-not-allowed'
                  }`}>
                  {loading
                    ? <span className="flex items-center justify-center gap-2">
                        <i className="fa-solid fa-spinner animate-spin" />Submitting...
                      </span>
                    : <span><i className="fa-solid fa-paper-plane mr-1.5" />Submit Review</span>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
